import React from 'react';
import { Box, Select, MenuItem, Typography } from '@mui/material';
import { DecimalNumberInput } from '../../../../components/ui';
import { SelectedProduct } from '../../../../types/sales.types';
import { formatCurrency } from '../../../../utils/formatters/money';
import { roundMoney } from '../../../../utils/money';
import {
  hasLineDiscount,
  hasLineMarkup,
  isLineDiscountInvalid,
} from '../../../../utils/sales/line-pricing.utils';
import { isRemoteCartItem } from '../../../../utils/sales/mixed-sale.utils';
import ProductImagePreview, { ProductImageData } from '../ProductImagePreview';
import RemoteOriginChip from '../RemoteOriginChip';
import * as S from '../../SalesStep2/SalesStep2.styles';

interface SalesLinePricingCardProps {
  product: SelectedProduct;
  /** Selling branch id — shows yellow origin chip for remote pieces */
  sellingLocationId?: number;
  editable: boolean;
  showMarkupControls: boolean;
  showIndividualMarkupControls: boolean;
  showDiscountControls: boolean;
  showIndividualDiscountControls: boolean;
  onMarkupChange: (
    productId: number,
    markupType: 'PERCENTAGE' | 'AMOUNT',
    markupValue: number,
  ) => void;
  onDiscountChange: (
    productId: number,
    discountType: 'PERCENTAGE' | 'AMOUNT',
    discountValue: number,
  ) => void;
  formatAdjustmentDisplay: (type: string, value: number, basePrice: number) => string;
}

const getProductImageData = (product: ProductImageData): ProductImageData => ({
  thumbnail: product.thumbnail ?? null,
  thumbnailUrl: product.thumbnailUrl ?? null,
  imagePath: product.imagePath ?? null,
});

const compactSelectSx = {
  minWidth: 64,
  '& .MuiSelect-select': { py: 0.4, fontSize: '0.75rem' },
};

const compactInputSx = {
  width: 76,
  '& .MuiInputBase-root': { fontSize: '0.75rem' },
  '& .MuiFormHelperText-root': { display: 'none' },
};

const lineControlsSx = {
  mt: 0.75,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flexWrap: 'wrap' as const,
};

const controlLabelSx = {
  fontSize: '0.7rem',
  fontWeight: 600,
  flexShrink: 0,
};

const SalesLinePricingCard: React.FC<SalesLinePricingCardProps> = ({
  product,
  sellingLocationId,
  editable,
  showMarkupControls,
  showIndividualMarkupControls,
  showDiscountControls,
  showIndividualDiscountControls,
  onMarkupChange,
  onDiscountChange,
  formatAdjustmentDisplay,
}) => {
  const productId = product.warehouseInventoryProduct.id;
  const isRemote = isRemoteCartItem(product, sellingLocationId);
  const originName = product.warehouseInventoryProduct.location?.name;
  const hasMarkup = hasLineMarkup(product.markupType, product.markupValue);
  const hasDiscount = hasLineDiscount(product.discountType, product.discountValue);
  const discountInvalid = isLineDiscountInvalid(
    product.adjustedPrice,
    product.discountType,
    product.discountValue,
  );
  const showLineMarkupControls = showMarkupControls && showIndividualMarkupControls;
  const showLineDiscountControls = showDiscountControls && showIndividualDiscountControls;
  const showLineControls = editable && (showLineMarkupControls || showLineDiscountControls);

  // Amount markup/discount labels follow stored adjusted/final so they match the order summary.
  const markupSummary = hasMarkup
    ? product.markupType === 'PERCENTAGE'
      ? `+${formatAdjustmentDisplay(product.markupType, product.markupValue, product.originalPrice)}`
      : `+${formatCurrency(roundMoney(Math.max(0, product.adjustedPrice - product.originalPrice)))}`
    : null;
  const discountSummary = hasDiscount
    ? product.discountType === 'PERCENTAGE'
      ? `−${formatAdjustmentDisplay(product.discountType, product.discountValue, product.adjustedPrice)}`
      : `−${formatCurrency(roundMoney(Math.max(0, product.adjustedPrice - product.finalPrice)))}`
    : null;

  return (
    <S.PricingLineCard>
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <ProductImagePreview
          product={getProductImageData(
            product.warehouseInventoryProduct.product as ProductImageData,
          )}
          alt={product.warehouseInventoryProduct.product.name}
          size={48}
          borderRadius={8}
          expandable={false}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: '0.875rem' }}
                noWrap
              >
                {product.warehouseInventoryProduct.product.name}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flexWrap: 'wrap',
                  mt: 0.15,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {product.warehouseInventoryProduct.productCodeUnique ?? 'N/A'}
                </Typography>
                {isRemote && <RemoteOriginChip locationName={originName} />}
              </Box>
            </Box>
            <Typography
              variant="body2"
              color="primary.main"
              sx={{ fontWeight: 700, flexShrink: 0, fontSize: '0.9375rem' }}
            >
              {formatCurrency(product.finalPrice)}
            </Typography>
          </Box>

          {(hasMarkup || hasDiscount) && (
            <Typography
              variant="caption"
              component="div"
              sx={{ display: 'block', mt: 0.35, fontSize: '0.75rem', lineHeight: 1.35 }}
            >
              <Box component="span" color="text.secondary">
                {formatCurrency(product.originalPrice)}
              </Box>
              {markupSummary != null && (
                <>
                  <Box component="span" color="text.secondary">
                    {' · '}
                  </Box>
                  <Box component="span" color="success.main">
                    {markupSummary}
                  </Box>
                </>
              )}
              {discountSummary != null && (
                <>
                  <Box component="span" color="text.secondary">
                    {' · '}
                  </Box>
                  <Box component="span" color="error.main">
                    {discountSummary}
                  </Box>
                </>
              )}
            </Typography>
          )}

          {showLineControls && (
            <Box sx={lineControlsSx}>
              {showLineMarkupControls && (
                <>
                  <Typography variant="caption" color="success.main" sx={controlLabelSx}>
                    Incremento
                  </Typography>
                  <Select
                    value={product.markupType === 'NONE' ? 'AMOUNT' : product.markupType}
                    onChange={(e) =>
                      onMarkupChange(
                        productId,
                        e.target.value as 'PERCENTAGE' | 'AMOUNT',
                        product.markupValue,
                      )
                    }
                    size="small"
                    disabled={!editable}
                    sx={compactSelectSx}
                  >
                    <MenuItem value="PERCENTAGE">%</MenuItem>
                    <MenuItem value="AMOUNT">Bs</MenuItem>
                  </Select>
                  <DecimalNumberInput
                    size="small"
                    value={product.markupValue > 0 ? product.markupValue : ''}
                    onChange={(val) =>
                      onMarkupChange(
                        productId,
                        product.markupType === 'NONE' ? 'AMOUNT' : product.markupType,
                        val ?? 0,
                      )
                    }
                    decimalPlaces={2}
                    min={0}
                    disabled={!editable}
                    sx={compactInputSx}
                  />
                </>
              )}

              {showLineDiscountControls && (
                <>
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ ...controlLabelSx, ml: showLineMarkupControls ? 0.5 : 0 }}
                  >
                    Descuento
                  </Typography>
                  <Select
                    value={product.discountType === 'NONE' ? 'AMOUNT' : product.discountType}
                    onChange={(e) =>
                      onDiscountChange(
                        productId,
                        e.target.value as 'PERCENTAGE' | 'AMOUNT',
                        product.discountValue,
                      )
                    }
                    size="small"
                    disabled={!editable}
                    sx={compactSelectSx}
                  >
                    <MenuItem value="PERCENTAGE">%</MenuItem>
                    <MenuItem value="AMOUNT">Bs</MenuItem>
                  </Select>
                  <DecimalNumberInput
                    size="small"
                    value={product.discountValue > 0 ? product.discountValue : ''}
                    onChange={(val) => {
                      const numValue = val ?? 0;
                      let validatedValue = numValue;
                      if (product.discountType === 'PERCENTAGE') {
                        validatedValue = Math.min(numValue, 99);
                      } else if (product.discountType === 'AMOUNT') {
                        validatedValue = Math.min(numValue, product.adjustedPrice);
                      }
                      onDiscountChange(
                        productId,
                        product.discountType === 'NONE' ? 'AMOUNT' : product.discountType,
                        validatedValue,
                      );
                    }}
                    decimalPlaces={2}
                    min={0}
                    max={product.discountType === 'PERCENTAGE' ? 99 : product.adjustedPrice}
                    disabled={!editable}
                    error={discountInvalid}
                    sx={compactInputSx}
                  />
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </S.PricingLineCard>
  );
};

export default SalesLinePricingCard;
